import {
  type RecurringKind,
  type RecurringTemplate,
  type RecurringTemplateRequest,
  recurringTemplateRequestSchema,
} from '@familywallet/shared';
import { z } from 'zod';
import { parsePositive } from './add-form';
import { groupAmountInput } from './money';

/** What the recurring form holds, as typed. */
export interface RecurringFormValues {
  name: string;
  kind: RecurringKind;
  amount: string;
  currency: string;
  accountId: string;
  toAccountId: string;
  categoryId: string;
  payeeName: string;
  description: string;
  frequency: 'monthly' | 'weekly';
  dayOfMonth: string;
  weekday: string;
  startOn: string;
  endOn: string;
  autoRecord: boolean;
}

interface Issue {
  path: string[];
  message: string;
}

const DATE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

const dayOfMonth = (raw: string): number | null => {
  if (!/^\d{1,2}$/.test(raw.trim())) return null;
  const value = Number.parseInt(raw.trim(), 10);
  return value >= 1 && value <= 31 ? value : null;
};

export function checkRecurringForm(v: RecurringFormValues): Issue[] {
  const issues: Issue[] = [];
  if (v.name.trim() === '') issues.push({ path: ['name'], message: 'recurring.errors.name' });
  const hasAmount = v.amount.trim() !== '';
  if (hasAmount && !parsePositive(v.amount, v.currency)) {
    issues.push({
      path: ['amount'],
      message:
        v.currency === 'LBP' && parsePositive(v.amount, 'USD')
          ? 'add.errors.amountWhole'
          : 'add.errors.amount',
    });
  }
  if (v.autoRecord && !hasAmount) {
    issues.push({ path: ['amount'], message: 'recurring.errors.autoNeedsAmount' });
  }
  if (v.accountId === '') issues.push({ path: ['accountId'], message: 'recurring.errors.account' });
  if (v.kind === 'transfer') {
    if (v.toAccountId === '' || v.toAccountId === v.accountId) {
      issues.push({ path: ['toAccountId'], message: 'recurring.errors.toAccount' });
    }
  } else if (v.categoryId === '') {
    issues.push({ path: ['categoryId'], message: 'recurring.errors.category' });
  }
  if (v.frequency === 'monthly' && dayOfMonth(v.dayOfMonth) === null) {
    issues.push({ path: ['dayOfMonth'], message: 'debts.errors.day' });
  }
  if (!DATE.test(v.startOn)) issues.push({ path: ['startOn'], message: 'recurring.errors.date' });
  if (v.endOn !== '' && (!DATE.test(v.endOn) || v.endOn < v.startOn)) {
    issues.push({ path: ['endOn'], message: 'recurring.errors.end' });
  }
  return issues;
}

/** The request for POST/PUT /recurring, checked by the shared schema the API uses. */
export function toRecurringRequest(v: RecurringFormValues): RecurringTemplateRequest {
  const transfer = v.kind === 'transfer';
  return recurringTemplateRequestSchema.parse({
    name: v.name,
    kind: v.kind,
    amount: v.amount.trim() === '' ? null : parsePositive(v.amount, v.currency),
    currency: v.currency,
    accountId: v.accountId,
    toAccountId: transfer ? v.toAccountId : null,
    categoryId: transfer ? null : v.categoryId,
    ...(!transfer && v.payeeName.trim() !== '' ? { payeeName: v.payeeName } : {}),
    description: v.description,
    autoRecord: v.autoRecord,
    frequency: v.frequency,
    dayOfMonth: v.frequency === 'monthly' ? dayOfMonth(v.dayOfMonth) : null,
    weekday: v.frequency === 'weekly' ? Number.parseInt(v.weekday, 10) : null,
    startOn: v.startOn,
    endOn: v.endOn === '' ? null : v.endOn,
  }) as RecurringTemplateRequest;
}

export function recurringFormSchema() {
  return z
    .custom<RecurringFormValues>()
    .superRefine((v, c) => {
      for (const issue of checkRecurringForm(v)) {
        c.addIssue({ code: 'custom', path: issue.path, message: issue.message });
      }
    })
    .transform((v) => toRecurringRequest(v));
}

/** The form filled from an existing template (editing), or blank. */
export function recurringFormValues(
  template: RecurringTemplate | undefined,
  fallback: { accountId: string; currency: string; today: string },
): RecurringFormValues {
  if (!template) {
    return {
      name: '',
      kind: 'expense',
      amount: '',
      currency: fallback.currency,
      accountId: fallback.accountId,
      toAccountId: '',
      categoryId: '',
      payeeName: '',
      description: '',
      frequency: 'monthly',
      dayOfMonth: fallback.today.slice(8, 10).replace(/^0/, ''),
      weekday: '1',
      startOn: fallback.today,
      endOn: '',
      autoRecord: false,
    };
  }
  return {
    name: template.name,
    kind: template.kind,
    amount: template.amount ? groupAmountInput(template.amount, template.currency) : '',
    currency: template.currency,
    accountId: template.account.id,
    toAccountId: template.toAccount?.id ?? '',
    categoryId: template.category?.id ?? '',
    payeeName: template.payee?.name ?? '',
    description: template.description,
    frequency: template.frequency,
    dayOfMonth: String(template.dayOfMonth ?? 1),
    weekday: String(template.weekday ?? 1),
    startOn: fallback.today > template.startOn ? fallback.today : template.startOn,
    endOn: template.endOn ?? '',
    autoRecord: template.autoRecord,
  };
}
